import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./LoginButton.css";
import { STATIC_URL } from "../../constants";

interface LoginButtonProps {
  onClick: () => void;
  isLoggedIn?: boolean;
  userNickname?: string;
  userAvatar?: string;
}

const LoginButton: React.FC<LoginButtonProps> = memo(
  ({ onClick, isLoggedIn, userNickname, userAvatar }) => {
    const { t } = useTranslation();
    
    const avatarUrl = useMemo(() => {
      if (!userAvatar) return null;

      // If it's already a full URL, return as is
      if (
        userAvatar.startsWith("http://") ||
        userAvatar.startsWith("https://")
      ) {
        return userAvatar;
      }

      // If it's a relative path, construct the full URL using Google Cloud Storage
      return `${STATIC_URL}${userAvatar.replace(/^\//, "")}`;
    }, [userAvatar]);

    const buttonTitle = useMemo(
      () => (isLoggedIn ? t("auth.loggedInAs", { nickname: userNickname }) : t("auth.loginTitle")),
      [isLoggedIn, userNickname, t]
    );

    const userInitial = useMemo(
      () => userNickname?.charAt(0).toUpperCase() || "U",
      [userNickname]
    );

    return (
      <button className="login-button" onClick={onClick} title={buttonTitle}>
        {isLoggedIn ? (
          avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`${userNickname}'s avatar`}
              className="avatar-button-image"
            />
          ) : (
            <div className="avatar-button-placeholder">{userInitial}</div>
          )
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10,17 15,12 10,7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
        )}
      </button>
    );
  }
);

LoginButton.displayName = "LoginButton";

export default LoginButton;
